/**
 * noteTemplateMerge — shared note-content ⇄ form-template merge logic.
 *
 * Oct 2026: extracted from App.js `openNote` so V2NoteHost uses the exact
 * same code path (V2 previously set editedNoteContent from the raw parsed
 * note content without merging against the template, losing matrix
 * structure, template field order and template-only props).
 */
import axios from 'axios';
import { formatDateMDY } from './dateFormat';

/**
 * Merge ONE saved field with its template definition.
 * Keeps full template structure (rowLabels, columnHeaders, width, …),
 * overrides with saved data, and prefers client-portal values.
 */
export const mergeFieldWithTemplate = (savedField, templateFields, clientFormData = {}) => {
  const templateField = templateFields?.find(tf => tf.id === savedField.id);
  if (!templateField) return savedField;

  const clientValue = clientFormData[savedField.id];
  const finalValue = clientValue !== undefined ? clientValue : savedField.value;

  return {
    ...templateField,           // Full template structure
    ...savedField,              // Override with saved data (id, label, type, value)
    value: finalValue,          // Client value if available, else saved value
    rowLabels: templateField.rowLabels,
    columnHeaders: templateField.columnHeaders,
    columnTypes: templateField.columnTypes,
    columnDropdownOptions: templateField.columnDropdownOptions,
    cellType: templateField.cellType,
    dropdownOptions: templateField.dropdownOptions,
    options: savedField.options?.length > 0 ? savedField.options : templateField.options,
    width: templateField.width,
    default_rows: templateField.default_rows,
  };
};

/**
 * Resolve the template a note was authored against.
 *  - Looks up `template_id` in the provided templates list, falling back
 *    to a direct GET /form-templates/{id} when the list doesn't have it.
 *  - When the note carries a `template_version` older than the current
 *    template, loads the historical snapshot so saved values line up.
 * Returns the resolved template object or null.
 */
export async function resolveNoteTemplate({ noteData, fullNote, templates, api }) {
  const templateId = noteData?.template_id || fullNote?.template_id;
  if (!templateId) return null;

  let template = (templates || []).find(t => t.id === templateId) || null;
  if (!template) {
    try {
      const r = await axios.get(`${api}/form-templates/${templateId}`);
      if (r?.data?.id) template = r.data;
    } catch { /* template may be deleted — merge degrades gracefully */ }
  }
  if (!template) return null;

  try {
    const noteTemplateVersion = fullNote?.template_version ?? noteData?.template_version;
    const currentTemplateVersion = template.version ?? 1;
    if (noteTemplateVersion && noteTemplateVersion < currentTemplateVersion) {
      const snapResp = await axios.get(
        `${api}/form-templates/${templateId}/version/${noteTemplateVersion}`,
      );
      if (snapResp?.data?.form_fields) {
        template = {
          ...template,
          form_fields: snapResp.data.form_fields,
          template_data: snapResp.data.template_data || template.template_data,
          _is_historical_version: true,
          _historical_version: noteTemplateVersion,
        };
      }
    }
  } catch (verErr) {
    console.warn('[noteTemplateMerge] Failed to load historical template version, falling back to current:', verErr);
  }
  return template;
}

/**
 * Merge a note's parsed content against its template:
 *  - sections branch: merge each section's fields with the template
 *  - flat fields branch: template field order is primary; saved values
 *    fill in by field ID; template-only fields appear empty; saved
 *    fields NOT in the template are preserved at the end
 *  - template_id / template_name preserved from the note when missing
 * Pure function — pass the already-resolved template (see
 * resolveNoteTemplate).  Returns the merged content object.
 */
export function mergeNoteContentWithTemplate(noteData, fullNote, template) {
  const clientFormData = fullNote?.client_form_data || {};
  const templateFields = template?.form_fields || [];

  const mergedContent = { ...noteData };
  if (!mergedContent.template_id && fullNote?.template_id) {
    mergedContent.template_id = fullNote.template_id;
  }
  if (!mergedContent.template_name && fullNote?.template_name) {
    mergedContent.template_name = fullNote.template_name;
  }

  if (noteData.sections) {
    mergedContent.sections = noteData.sections.map(section => ({
      ...section,
      fields: section.fields?.map(field => mergeFieldWithTemplate(field, templateFields, clientFormData)),
    }));
  } else if (noteData.fields) {
    if (templateFields.length > 0) {
      const savedFieldsMap = new Map(noteData.fields.map(f => [f.id, f]));
      mergedContent.fields = templateFields.map(tf => {
        const savedField = savedFieldsMap.get(tf.id);
        if (savedField) {
          return mergeFieldWithTemplate(savedField, templateFields, clientFormData);
        }
        return { ...tf, value: tf.default_value || '' };
      });
      // Preserve saved fields not present in the template (edge case)
      const templateFieldIds = new Set(templateFields.map(tf => tf.id));
      const extraSavedFields = noteData.fields
        .filter(f => !templateFieldIds.has(f.id))
        .map(f => mergeFieldWithTemplate(f, templateFields, clientFormData));
      if (extraSavedFields.length > 0) {
        mergedContent.fields = [...mergedContent.fields, ...extraSavedFields];
      }
    } else {
      mergedContent.fields = noteData.fields.map(field => mergeFieldWithTemplate(field, templateFields, clientFormData));
    }
  }
  return mergedContent;
}

/**
 * Initialize note content from its template when the saved content is
 * MISSING (empty / blank-parse).  Mirror of legacy App.js openNote's
 * empty-content branch:
 *  - legacy imported notes fill values from `fullNote.form_data`
 *  - client_* field types auto-populate from the client record
 *  - diagnoses / medications snapshot fields populate from active rows
 *    (client_id stamped on every row — PHI cross-leak guard)
 *  - plain-text legacy content is carried into the first free text field
 * Returning a template-shaped `fields` array is ALSO what unblocks the
 * autosave engine's draft check (it defers while formData is empty), so
 * empty-note auto-restore / the manual recovery banner can run.
 */
export function initializeNoteContentFromTemplate({
  fullNote,
  template,
  client = null,
  diagnoses = [],
  medications = [],
  legacyPlainText = null,
}) {
  const legacyFormData = fullNote?.form_data || {};
  const noteClientId = fullNote?.client_id;

  const getAutoPopulatedValue = (field) => {
    switch (field.type) {
      case 'client_name':
        return client?.full_name || '';
      case 'client_dob': {
        const dobVal = client?.dob || client?.date_of_birth;
        return dobVal ? formatDateMDY(dobVal) : '';
      }
      case 'client_age': {
        const dob = client?.dob || client?.date_of_birth;
        const ageYears = dob ? Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000)) : null;
        return ageYears !== null ? `${ageYears} years old` : '';
      }
      case 'client_email':
        return client?.email || '';
      case 'client_phone':
        return client?.phone || '';
      case 'client_address':
        return [client?.address || client?.street || client?.address_line1, client?.address_line2, client?.city, client?.state, client?.zipcode || client?.zip || client?.zip_code].filter(Boolean).join(', ') || '';
      case 'client_allergies':
        return client?.allergies || 'No known allergies';
      case 'client_diagnoses': {
        const clientDx = (diagnoses || []).filter(d => d.client_id === noteClientId && d.is_active);
        return clientDx.length > 0 ? JSON.stringify(clientDx.map(dx => ({
          id: dx.id,
          client_id: dx.client_id || noteClientId,
          code: dx.code || dx.diagnosis_code,
          description: dx.description || dx.diagnosis_description,
          diagnosis_tag: dx.diagnosis_tag,
          start_date: dx.start_date,
          assigned_by_name: dx.assigned_by_name || 'Unknown',
          provider_names: dx.provider_names || [],
          is_primary: dx.is_primary,
        }))) : '';
      }
      case 'client_medications': {
        const clientMeds = (medications || []).filter(m => m.client_id === noteClientId && m.is_active === true);
        return clientMeds.length > 0 ? JSON.stringify(clientMeds.map(med => ({
          id: med.id,
          client_id: med.client_id || noteClientId,
          medication_name: med.medication_name,
          dosage: med.dosage,
          frequency: med.frequency,
          prescriber: med.prescriber,
          purpose: med.purpose,
        }))) : '';
      }
      default:
        return null;
    }
  };

  let content;
  if (template?.form_fields?.length > 0) {
    content = {
      template_id: fullNote?.template_id,
      template_name: template.name || fullNote?.template_name,
      fields: template.form_fields.map(field => {
        if (legacyFormData[field.id] !== undefined) {
          return { ...field, value: legacyFormData[field.id] };
        }
        const autoValue = getAutoPopulatedValue(field);
        if (autoValue !== null && autoValue !== '') {
          return { ...field, value: autoValue };
        }
        return { ...field, value: field.default_value || '' };
      }),
    };
  } else {
    content = {
      fields: [],
      ...(fullNote?.template_id ? { template_id: fullNote.template_id, template_name: fullNote.template_name } : {}),
    };
  }

  // Carry legacy plain-text content into the first empty free-text field.
  if (legacyPlainText) {
    const target = content.fields.find(f =>
      ['textarea', 'text', 'richtext', 'narrative'].includes(f.type) && !f.value);
    if (target) {
      target.value = legacyPlainText;
    } else {
      content.fields.push({
        id: 'legacy_plain_text', label: 'Imported Note Text',
        type: 'textarea', value: legacyPlainText,
      });
    }
  }
  return content;
}

/**
 * Classify raw note content the same way legacy openNote does:
 *  - JSON-looking content that fails to parse = CORRUPT → open view-only
 *    (never blank-init over real content; autosave would persist the wipe)
 *  - non-JSON plain text = legacy content → carry into template init
 * Returns { corrupt, legacyPlainText }.
 */
export function classifyUnparsedContent(rawContentString) {
  const rawContent = typeof rawContentString === 'string' ? rawContentString.trim() : '';
  if (!rawContent || ['{}', 'null', '""', '[]'].includes(rawContent)) {
    return { corrupt: false, legacyPlainText: null };
  }
  if (rawContent.startsWith('{') || rawContent.startsWith('[')) {
    return { corrupt: true, legacyPlainText: null };
  }
  return { corrupt: false, legacyPlainText: rawContent };
}
